import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Stop as StopIcon,
  AddComment as NewChatIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';

// ─── Suggested starter questions ──────────────────────────────────────────

const STARTER_QUESTIONS = [
  'Show me all projects currently over budget',
  'Which projects have the most AR outstanding?',
  "What's the labor variance across all active projects?",
  "Who are Jason Smith's active projects?",
  'Show me projects in the Pre-Final phase',
  'Which projects have overdue tasks?',
];

// ─── Markdown rendering styles ─────────────────────────────────────────────

const mdComponents = {
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1.5 }}>
      <table style={{
        borderCollapse: 'collapse',
        width: '100%',
        fontSize: '0.85rem',
        minWidth: 400,
      }}>
        {children}
      </table>
    </Box>
  ),
  thead: ({ children }) => (
    <thead style={{ backgroundColor: '#1A5490', color: '#fff' }}>{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children, ...props }) => (
    <tr style={{
      borderBottom: '1px solid #e0e0e0',
    }} {...props}>{children}</tr>
  ),
  th: ({ children }) => (
    <th style={{
      padding: '8px 12px',
      textAlign: 'left',
      fontWeight: 600,
      color: '#fff',
      whiteSpace: 'nowrap',
    }}>{children}</th>
  ),
  td: ({ children }) => (
    <td style={{
      padding: '7px 12px',
      verticalAlign: 'top',
    }}>{children}</td>
  ),
  h2: ({ children }) => (
    <Typography variant="h6" sx={{ mt: 2, mb: 0.5, fontWeight: 700, color: '#1A5490' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.25, fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.65 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, mb: 1, '& li': { mb: 0.25 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1, '& li': { mb: 0.25 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ lineHeight: 1.55 }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>
  ),
  code: ({ inline, children }) => inline ? (
    <Box component="code" sx={{
      backgroundColor: '#f0f0f0',
      borderRadius: 0.5,
      px: 0.5,
      fontFamily: 'monospace',
      fontSize: '0.82em',
    }}>{children}</Box>
  ) : (
    <Box component="pre" sx={{
      backgroundColor: '#1e1e1e',
      color: '#d4d4d4',
      borderRadius: 1,
      p: 1.5,
      overflowX: 'auto',
      fontSize: '0.82rem',
      fontFamily: 'monospace',
      my: 1,
    }}>
      <code>{children}</code>
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box sx={{
      borderLeft: '4px solid #1A5490',
      pl: 2,
      ml: 0,
      my: 1,
      color: 'text.secondary',
    }}>
      {children}
    </Box>
  ),
  hr: () => <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />,
};

// ─── Elapsed timer hook ────────────────────────────────────────────────────

function useElapsedTimer(active) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [active]);
  return elapsed;
}

// ─── Main Component ────────────────────────────────────────────────────────

const STORAGE_KEY = 'askacs_conversation';

export default function AskACS() {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const abortRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const elapsed = useElapsedTimer(loading);

  // Persist conversation to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // ignore quota errors
    }
  }, [messages]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const sendMessage = useCallback(async (text) => {
    const query = (text || input).trim();
    if (!query || loading) return;

    setInput('');
    setError(null);

    const userMsg = { role: 'user', content: query, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Build history BEFORE adding the new user message (API adds it)
    const history = messages.slice(-20).map(m => ({
      role: m.role,
      content: m.content,
    }));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/llm/query',
        { query, conversationHistory: history },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          timeout: 120000,
        }
      );

      const assistantMsg = {
        role: 'assistant',
        content: response.data.answer,
        id: Date.now() + 1,
        toolsUsed: response.data.toolsUsed || [],
        iterations: response.data.iterations,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') {
        // User cancelled
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '_Query cancelled._',
          id: Date.now() + 1,
          cancelled: true,
        }]);
      } else {
        const errMsg = err.response?.data?.error || err.message || 'An error occurred';
        setError(errMsg);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ **Error:** ${errMsg}`,
          id: Date.now() + 1,
          isError: true,
        }]);
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading, messages]);

  const handleStop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setError(null);
    setInput('');
    localStorage.removeItem(STORAGE_KEY);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f5f7fa',
    }}>
      {/* ── Header ── */}
      <Box sx={{
        px: 3,
        py: 1.5,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1A5490 0%, #1a6bc4 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <SparkleIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#1A5490' }}>
              Ask ACS
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AI-powered answers about any project
            </Typography>
          </Box>
        </Box>
        <Tooltip title="New conversation">
          <Button
            size="small"
            startIcon={<NewChatIcon />}
            onClick={handleNewConversation}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            New Chat
          </Button>
        </Tooltip>
      </Box>

      {/* ── Messages Area ── */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
        {/* Empty state with suggested questions */}
        {messages.length === 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 3,
            py: 4,
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1A5490 0%, #1a6bc4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 8px 24px rgba(17,68,122,0.3)',
              }}>
                <SparkleIcon sx={{ color: '#fff', fontSize: 36 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A5490', mb: 0.5 }}>
                Ask me anything
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                I have access to all ACS project data, D-Tools proposals, and can search the web for equipment info.
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'center',
              maxWidth: 680,
            }}>
              {STARTER_QUESTIONS.map((q) => (
                <Chip
                  key={q}
                  label={q}
                  onClick={() => sendMessage(q)}
                  clickable
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    border: '1px solid #d0d7de',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                      borderColor: '#1A5490',
                    },
                    fontSize: '0.82rem',
                    height: 'auto',
                    py: 0.75,
                    px: 0.5,
                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Message list */}
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              gap: 1.5,
              maxWidth: 820,
              width: '100%',
              mx: 'auto',
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            {/* Avatar */}
            <Box sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.5,
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #1A5490 0%, #1a6bc4 100%)'
                : 'linear-gradient(135deg, #FF6B35 0%, #e8853a 100%)',
            }}>
              {msg.role === 'user'
                ? <PersonIcon sx={{ color: '#fff', fontSize: 18 }} />
                : <AIIcon sx={{ color: '#fff', fontSize: 18 }} />
              }
            </Box>

            {/* Bubble */}
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                maxWidth: 'calc(100% - 50px)',
                borderRadius: msg.role === 'user'
                  ? '18px 4px 18px 18px'
                  : '4px 18px 18px 18px',
                backgroundColor: msg.role === 'user' ? '#1A5490' : '#fff',
                color: msg.role === 'user' ? '#fff' : 'inherit',
                border: msg.role === 'assistant' ? '1px solid #e0e0e0' : 'none',
              }}
            >
              {msg.role === 'user' ? (
                <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              ) : (
                <Box sx={{
                  '& > *:first-of-type': { mt: 0 },
                  '& > *:last-child': { mb: 0 },
                  '& tr:nth-of-type(even)': { backgroundColor: '#f9fafb' },
                }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={mdComponents}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </Box>
              )}

              {/* Tool usage indicator */}
              {msg.role === 'assistant' && msg.toolsUsed && msg.toolsUsed.length > 0 && (
                <Box sx={{
                  mt: 1.5,
                  pt: 1,
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                }}>
                  {[...new Set(msg.toolsUsed.map(t => t.tool))].map(toolName => (
                    <Chip
                      key={toolName}
                      label={toolName.replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        fontSize: '0.68rem',
                        height: 20,
                        backgroundColor: '#f0f4ff',
                        color: '#1A5490',
                        border: '1px solid #ccd9f0',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        ))}

        {/* Loading indicator */}
        {loading && (
          <Box sx={{
            display: 'flex',
            gap: 1.5,
            maxWidth: 820,
            width: '100%',
            mx: 'auto',
          }}>
            <Box sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.5,
              background: 'linear-gradient(135deg, #FF6B35 0%, #e8853a 100%)',
            }}>
              <AIIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: '4px 18px 18px 18px',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress size={16} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Thinking{elapsed > 0 ? ` (${elapsed}s)` : '…'}
              </Typography>
            </Paper>
          </Box>
        )}

        <div ref={bottomRef} />
      </Box>

      {/* ── Input Bar ── */}
      <Box sx={{
        px: { xs: 2, md: 4 },
        py: 2,
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        flexShrink: 0,
      }}>
        <Box sx={{
          maxWidth: 820,
          mx: 'auto',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}>
          <TextField
            inputRef={inputRef}
            multiline
            maxRows={6}
            fullWidth
            placeholder="Ask about any project, equipment, financials, labor…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f8f9fa',
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                },
              },
            }}
            helperText={
              <Typography variant="caption" color="text.disabled">
                Press Enter to send · Shift+Enter for new line
              </Typography>
            }
            FormHelperTextProps={{ sx: { mx: 0.5 } }}
          />

          {loading ? (
            <Tooltip title="Stop">
              <IconButton
                onClick={handleStop}
                sx={{
                  mb: 2.5,
                  backgroundColor: '#c62828',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#b71c1c' },
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                }}
              >
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Send (Enter)">
              <span>
                <IconButton
                  onClick={() => sendMessage()}
                  disabled={!input.trim()}
                  sx={{
                    mb: 2.5,
                    backgroundColor: input.trim() ? '#1A5490' : undefined,
                    color: input.trim() ? '#fff' : undefined,
                    '&:hover': { backgroundColor: input.trim() ? '#0d3560' : undefined },
                    '&.Mui-disabled': { opacity: 0.4 },
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}
